import { useState, useMemo } from 'react';

/**
 * Custom hook for sorting data in list pages
 * @param {Array} data - The array of items to sort
 * @param {Object} config - Configuration object
 * @param {string} config.defaultSort - Default sort value (default: 'name-asc')
 * @param {string} config.nameField - Field to use for name sorting (default: 'name')
 * @param {string} config.dateField - Field to use for date sorting (default: 'created_at')
 * @returns {Object} - { sortedData, sortBy, setSortBy, sortOptions }
 */
export const useSortableData = (data, config = {}) => {
  const {
    defaultSort = 'name-asc',
    nameField = 'name',
    dateField = 'created_at'
  } = config;

  const [sortBy, setSortBy] = useState(defaultSort);

  const sortOptions = [
    { value: 'name-asc', label: 'A-Z' },
    { value: 'name-desc', label: 'Z-A' },
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' }
  ];

  const sortedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    
    return [...data].sort((a, b) => {
      const nameA = (a[nameField] || '').toString();
      const nameB = (b[nameField] || '').toString();
      const dateA = new Date(a[dateField] || 0);
      const dateB = new Date(b[dateField] || 0);

      switch (sortBy) {
        case 'name-asc':
          return nameA.localeCompare(nameB);
        case 'name-desc':
          return nameB.localeCompare(nameA);
        case 'newest':
          return dateB - dateA;
        case 'oldest':
          return dateA - dateB;
        default:
          return 0;
      }
    });
  }, [data, sortBy, nameField, dateField]);

  return {
    sortedData,
    sortBy,
    setSortBy,
    sortOptions
  };
};

/**
 * Sort component for consistent UI across pages
 */
export const SortSelect = ({ sortBy, setSortBy, sortOptions, className = '' }) => {
  // This is just a helper - actual rendering should be done in the component
  // to use Shadcn UI components properly
  return { sortBy, setSortBy, sortOptions, className };
};

export default useSortableData;
